import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import Topbar from "@/components/layout/Topbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  ArrowCounterClockwise, MagnifyingGlass, ClockCounterClockwise, ArrowsClockwise,
  Plus, PencilSimple, Trash, Eye, ShieldCheck, Warning, User,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import NumberInput from "@/components/ui/NumberInput";

const ACTION_META = {
  create: { label: "Alta",      color: "bg-emerald-100 text-emerald-800", icon: Plus },
  update: { label: "Modif.",    color: "bg-amber-100 text-amber-800",     icon: PencilSimple },
  delete: { label: "Baja",      color: "bg-rose-100 text-rose-800",       icon: Trash },
};

const RESOURCE_LABEL = {
  clientes: "Clientes", proveedores: "Proveedores", productos: "Productos",
  insumos: "Insumos", cuentas: "Plan de Cuentas", cajas: "Cajas",
  "medios-pago": "Medios de Pago", users: "Usuarios",
  pedidos: "Pedidos", rubros: "Rubros", "listas-precios": "Listas de Precios",
  facturas: "Facturas", "facturas-compra": "Facturas de compra",
  "cobros-pagos": "Cobros / Pagos", cheques: "Cheques recibidos",
  "cheques-emitidos": "Cheques emitidos",
  rec: "Remitos", asientos: "Asientos", promociones: "Promociones",
  produccion: "Producción", recepcion: "Recepción",
  tiendanube: "Tienda Nube", logistica: "Logística",
  "movimientos-caja": "Movimiento de caja",
  "pasivos-deudas": "Pasivos / Deudas",
  transformaciones: "Transformaciones",
  lotes: "Lotes",
  "listas-precios-items": "Ítems de lista de precios",
};

// Etiquetas legibles por nombre de campo (aparecen en chips y diffs)
const FIELD_LABEL = {
  nombre: "Nombre", razon_social: "Razón social", cuit: "CUIT",
  telefono: "Teléfono", email: "Email", direccion: "Dirección",
  codigo: "Código", descripcion: "Descripción",
  precio: "Precio", precio_actual: "Precio", precio_lista: "Precio lista",
  saldo: "Saldo", saldo_ars: "Saldo ARS", saldo_usd: "Saldo USD",
  monto: "Monto", total: "Total", subtotal: "Subtotal",
  iva: "IVA", iva_pct: "% IVA", descuento_pct: "% Descuento", bonificacion_pct: "% Bonif.",
  tipo: "Tipo", moneda: "Moneda", scope: "Ámbito",
  numero: "Número", numero_documento: "Nº Documento",
  tipo_comprobante: "Comprobante", estado: "Estado",
  fecha: "Fecha", fecha_emision: "Fecha emisión", fecha_cobro: "Fecha cobro",
  fecha_vencimiento: "Vencimiento", fecha_entrega: "Fecha entrega",
  banco_code: "Banco (código)", banco_nombre: "Banco",
  caja_id: "Caja", caja_nombre: "Caja", caja_default_nombre: "Caja default",
  caja_origen_nombre: "Caja origen", caja_destino_nombre: "Caja destino",
  contraparte_nombre: "Contraparte", cliente_nombre: "Cliente",
  proveedor_nombre: "Proveedor", firmante: "Firmante",
  concepto: "Concepto", observaciones: "Observaciones",
  is_admin: "Es admin", role: "Rol", username: "Usuario",
  permissions: "Permisos", activa: "Activa", activo: "Activo",
  clasificado: "Clasificado", cuenta_codigo: "Cta. contable",
  kilos: "Kilos", kilos_disponibles: "Kg disp.",
  cantidad: "Cantidad", cantidad_bultos: "Bultos",
  producto_nombre: "Producto", producto_normalizado: "Producto",
  tipo_proveedor: "Tipo prov.", auto_actualiza_stock: "Auto stock",
  auto_actualiza_costo: "Auto costo", debe: "Debe", haber: "Haber",
  entidad_nombre: "Entidad", tipo_pasivo: "Tipo pasivo",
  referencia_tipo: "Origen", referencia_id: "Ref.",
};

// Sets por comportamiento de formato
const CURRENCY_FIELDS = new Set([
  "monto", "total", "subtotal", "importe", "precio", "precio_actual", "precio_lista",
  "saldo", "debe", "haber", "iva", "costo_por_kg", "costo",
]);
const PCT_FIELDS = new Set(["iva_pct", "descuento_pct", "bonificacion_pct"]);
const DATE_FIELDS = new Set([
  "fecha", "fecha_emision", "fecha_cobro", "fecha_vencimiento", "fecha_entrega",
  "depositado_at", "anulado_at", "rechazado_at", "rescatado_at",
]);
const BOOL_FIELDS = new Set([
  "is_admin", "activa", "activo", "clasificado",
  "auto_actualiza_stock", "auto_actualiza_costo",
]);

function fmtDate(iso) {
  if (!iso) return "-";
  const d = new Date(iso);
  return d.toLocaleString("es-AR", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" });
}

function fmtDateShort(iso) {
  if (!iso) return "—";
  const s = String(iso);
  // ISO date "YYYY-MM-DD" o timestamp completo
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${m[3]}/${m[2]}/${m[1].slice(2)}`;
  return s;
}

function fmtMoneyAR(n, moneda = "ARS") {
  const num = Number(n);
  if (!isFinite(num)) return String(n);
  const symbol = moneda === "USD" ? "US$" : "$";
  return `${symbol} ${num.toLocaleString("es-AR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function fieldLabel(key) {
  return FIELD_LABEL[key] || key.replace(/_/g, " ");
}

// Formatea un valor según el nombre de campo (dinero, %, fecha, bool).
function fmtByKey(key, v, snap) {
  if (v == null || v === "") return "—";
  if (typeof v === "object") {
    if (Array.isArray(v)) return `${v.length} ítem${v.length === 1 ? "" : "s"}`;
    return JSON.stringify(v).slice(0, 60);
  }
  if (BOOL_FIELDS.has(key) || typeof v === "boolean") {
    return v === true || v === "true" ? "Sí" : "No";
  }
  if (CURRENCY_FIELDS.has(key) || /_ars$|_usd$/i.test(key)) {
    const moneda = /_usd$/i.test(key) ? "USD" : (snap?.moneda || "ARS");
    return fmtMoneyAR(v, moneda);
  }
  if (PCT_FIELDS.has(key)) return `${Number(v)}%`;
  if (DATE_FIELDS.has(key) && typeof v === "string") return fmtDateShort(v);
  return String(v);
}

// Campos "nombre" preferidos por tipo de recurso para armar un label humano.
const LABEL_FIELDS = {
  clientes: ["nombre", "razon_social"],
  proveedores: ["nombre", "razon_social"],
  productos: ["nombre", "descripcion", "codigo"],
  insumos: ["nombre", "descripcion", "codigo"],
  cuentas: ["nombre", "codigo"],
  cajas: ["nombre"],
  "medios-pago": ["nombre"],
  users: ["username", "name"],
  pedidos: ["numero", "cliente_nombre"],
  rubros: ["nombre"],
  "listas-precios": ["nombre"],
  facturas: ["numero", "tipo_comprobante"],
  "facturas-compra": ["numero", "tipo_comprobante"],
  "cobros-pagos": ["concepto", "id"],
  cheques: ["numero", "banco_nombre"],
  "cheques-emitidos": ["numero", "banco_nombre"],
  rec: ["numero"],
  asientos: ["numero", "concepto"],
  promociones: ["nombre"],
  produccion: ["numero"],
  recepcion: ["numero"],
  "movimientos-caja": ["concepto"],
  "pasivos-deudas": ["entidad_nombre", "concepto"],
  transformaciones: ["numero", "id"],
  lotes: ["numero", "descripcion"],
};

// Campos secundarios que hacen el resumen más útil (CUIT, monto, etc.).
const CONTEXT_FIELDS = {
  clientes: ["cuit", "telefono", "saldo_ars"],
  proveedores: ["cuit", "tipo_proveedor", "saldo_ars"],
  productos: ["codigo", "precio_actual"],
  insumos: ["codigo"],
  cuentas: ["codigo", "tipo"],
  cajas: ["tipo", "moneda", "saldo"],
  "medios-pago": ["tipo", "scope", "caja_default_nombre"],
  users: ["is_admin"],
  pedidos: ["cliente_nombre", "estado"],
  facturas: ["contraparte_nombre", "total", "estado", "fecha"],
  "facturas-compra": ["contraparte_nombre", "total", "estado", "fecha"],
  "cobros-pagos": ["caja_nombre", "monto", "contraparte_nombre", "fecha"],
  cheques: ["monto", "banco_nombre", "fecha_cobro", "firmante"],
  "cheques-emitidos": ["monto", "banco_nombre", "proveedor_nombre", "fecha_cobro", "estado"],
  rec: ["estado", "fecha"],
  asientos: ["fecha", "debe"],
  promociones: ["descuento_pct", "activa"],
  "movimientos-caja": ["caja_nombre", "tipo", "monto", "fecha"],
  "pasivos-deudas": ["tipo_pasivo", "saldo", "moneda", "fecha_vencimiento"],
  transformaciones: ["fecha"],
  lotes: ["producto_normalizado", "kilos_disponibles"],
};

function pickLabel(resource, snap) {
  if (!snap) return null;
  const fields = LABEL_FIELDS[resource] || ["nombre", "numero", "descripcion"];
  for (const f of fields) {
    if (snap[f] != null && snap[f] !== "") return String(snap[f]);
  }
  return null;
}

function pickContext(resource, snap) {
  if (!snap) return [];
  const fields = CONTEXT_FIELDS[resource] || [];
  return fields
    .map((f) => {
      const v = snap[f];
      if (v == null || v === "") return null;
      return { key: f, value: v };
    })
    .filter(Boolean);
}

// Genera una oración descriptiva del evento (qué se hizo, sobre qué, cuánto).
function describeEvent(rec) {
  const snap = rec.after || rec.before || rec.payload || {};
  const action = rec.action;
  const verb = action === "create" ? "Alta" : action === "update" ? "Modificación" : action === "delete" ? "Baja" : action;
  const monto = snap.monto ?? snap.total ?? snap.saldo;
  const monedaSnap = snap.moneda || "ARS";

  switch (rec.resource) {
    case "facturas": {
      const t = snap.tipo_comprobante || "Comprobante";
      const n = snap.numero ? ` ${snap.numero}` : "";
      const parte = snap.contraparte_nombre ? ` a ${snap.contraparte_nombre}` : "";
      const imp = snap.total != null ? ` por ${fmtMoneyAR(snap.total, monedaSnap)}` : "";
      return `${verb} de ${t}${n}${parte}${imp}`;
    }
    case "facturas-compra": {
      const t = snap.tipo_comprobante || "Comprobante";
      const n = snap.numero ? ` ${snap.numero}` : "";
      const parte = snap.contraparte_nombre ? ` de ${snap.contraparte_nombre}` : "";
      const imp = snap.total != null ? ` por ${fmtMoneyAR(snap.total, monedaSnap)}` : "";
      return `${verb} de compra ${t}${n}${parte}${imp}`;
    }
    case "cobros-pagos": {
      const es = snap.tipo === "cobro" ? "Cobro" : snap.tipo === "pago" ? "Pago" : "Cobro/Pago";
      const parte = snap.contraparte_nombre ? ` ${snap.tipo === "pago" ? "a" : "de"} ${snap.contraparte_nombre}` : "";
      const caja = snap.caja_nombre ? ` en ${snap.caja_nombre}` : "";
      const imp = snap.monto != null ? ` por ${fmtMoneyAR(snap.monto, monedaSnap)}` : "";
      if (action === "delete") return `Anulación de ${es.toLowerCase()}${parte}${imp}${caja}`;
      return `${es}${parte}${imp}${caja}`;
    }
    case "cheques": {
      const n = snap.numero ? ` N° ${snap.numero}` : "";
      const b = snap.banco_nombre ? ` (${snap.banco_nombre})` : "";
      const parte = snap.cliente_nombre ? ` de ${snap.cliente_nombre}` : (snap.firmante ? ` firmante ${snap.firmante}` : "");
      const imp = snap.monto != null ? ` por ${fmtMoneyAR(snap.monto, monedaSnap)}` : "";
      const venc = snap.fecha_cobro ? ` — vence ${fmtDateShort(snap.fecha_cobro)}` : "";
      return `${verb} de cheque recibido${n}${b}${parte}${imp}${venc}`;
    }
    case "cheques-emitidos": {
      const n = snap.numero ? ` N° ${snap.numero}` : "";
      const b = snap.banco_nombre ? ` (${snap.banco_nombre})` : "";
      const parte = snap.proveedor_nombre ? ` a ${snap.proveedor_nombre}` : "";
      const imp = snap.monto != null ? ` por ${fmtMoneyAR(snap.monto, monedaSnap)}` : "";
      const venc = snap.fecha_cobro ? ` — vence ${fmtDateShort(snap.fecha_cobro)}` : "";
      return `${verb} de cheque emitido${n}${b}${parte}${imp}${venc}`;
    }
    case "movimientos-caja": {
      const tipo = snap.referencia_tipo === "transferencia_ingreso" ? "Ingreso por transferencia"
                 : snap.referencia_tipo === "transferencia_egreso" ? "Egreso por transferencia"
                 : snap.referencia_tipo === "arqueo_caja" ? "Arqueo"
                 : snap.tipo === "ingreso" ? "Ingreso"
                 : snap.tipo === "egreso" ? "Egreso"
                 : "Movimiento";
      const caja = snap.caja_nombre ? ` en ${snap.caja_nombre}` : "";
      const c = snap.concepto ? ` "${snap.concepto}"` : "";
      const imp = snap.monto != null ? ` por ${fmtMoneyAR(snap.monto, monedaSnap)}` : "";
      if (action === "delete") return `Eliminación de ${tipo.toLowerCase()}${caja}${c}${imp}`;
      return `${tipo}${caja}${c}${imp}`;
    }
    case "cajas": {
      const n = snap.nombre ? ` "${snap.nombre}"` : "";
      const t = snap.tipo ? ` (${snap.tipo})` : "";
      return `${verb} de caja${n}${t}`;
    }
    case "clientes":
    case "proveedores": {
      const n = snap.nombre || snap.razon_social || "";
      const cuit = snap.cuit ? ` · CUIT ${snap.cuit}` : "";
      return `${verb} de ${rec.resource === "clientes" ? "cliente" : "proveedor"} ${n}${cuit}`;
    }
    case "productos":
    case "insumos": {
      const n = snap.nombre || snap.descripcion || "";
      const cod = snap.codigo ? ` [${snap.codigo}]` : "";
      const p = snap.precio_actual != null ? ` · ${fmtMoneyAR(snap.precio_actual)}` : "";
      return `${verb} de ${rec.resource === "productos" ? "producto" : "insumo"} ${n}${cod}${p}`;
    }
    case "users": {
      const u = snap.username || snap.name || "";
      const adm = snap.is_admin ? " (admin)" : "";
      return `${verb} de usuario ${u}${adm}`;
    }
    case "pasivos-deudas": {
      const e = snap.entidad_nombre || "";
      const c = snap.concepto ? ` · ${snap.concepto}` : "";
      const imp = snap.saldo != null ? ` por ${fmtMoneyAR(snap.saldo, monedaSnap)}` : "";
      return `${verb} de pasivo ${e}${c}${imp}`;
    }
    case "asientos": {
      const n = snap.numero ? ` N° ${snap.numero}` : "";
      const c = snap.concepto ? ` — ${snap.concepto}` : "";
      const imp = snap.debe != null ? ` · Debe ${fmtMoneyAR(snap.debe)}` : "";
      return `${verb} de asiento contable${n}${c}${imp}`;
    }
    case "medios-pago": {
      const n = snap.nombre ? ` "${snap.nombre}"` : "";
      const t = snap.tipo ? ` (${snap.tipo})` : "";
      return `${verb} de medio de pago${n}${t}`;
    }
    default: {
      const lbl = pickLabel(rec.resource, snap);
      const nameLbl = lbl ? ` "${lbl}"` : "";
      const impLbl = monto != null && typeof monto === "number" ? ` · ${fmtMoneyAR(monto, monedaSnap)}` : "";
      return `${verb} en ${RESOURCE_LABEL[rec.resource] || rec.resource || "registro"}${nameLbl}${impLbl}`;
    }
  }
}

function summaryFor(rec) {
  const snap = rec.after || rec.before || rec.payload || {};
  const label = pickLabel(rec.resource, snap);
  const context = pickContext(rec.resource, snap);
  // Para updates: identificar hasta 3 campos que cambiaron con "antes → después".
  const changes = [];
  if (rec.action === "update" && rec.before && rec.after) {
    for (const k of Object.keys({ ...rec.before, ...rec.after })) {
      if (["_id", "id", "created_at", "updated_at", "password_hash", "overrides"].includes(k)) continue;
      const b = rec.before[k]; const a = rec.after[k];
      if (JSON.stringify(b) !== JSON.stringify(a)) {
        changes.push({ key: k, before: b, after: a });
      }
      if (changes.length >= 3) break;
    }
  }
  const description = describeEvent(rec);
  return { label, context, changes, description, snap };
}

// Diffs compactos: filtra keys internas y campos que no cambiaron.
function diffKeys(before, after) {
  if (!before || !after) return [];
  const keys = new Set([...Object.keys(before), ...Object.keys(after)]);
  const out = [];
  for (const k of keys) {
    if (["_id", "id", "created_at", "updated_at", "password_hash"].includes(k)) continue;
    const b = before[k]; const a = after[k];
    if (JSON.stringify(b) !== JSON.stringify(a)) out.push({ key: k, before: b, after: a });
  }
  return out;
}

export default function AuditoriaPage() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ q: "", user_id: "", resource: "", action: "" });
  const [skip, setSkip] = useState(0);
  const limit = 50;
  const [users, setUsers] = useState([]);
  const [detail, setDetail] = useState(null);
  const [confirmRevert, setConfirmRevert] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [retentionDays, setRetentionDays] = useState(30);
  const [savingSettings, setSavingSettings] = useState(false);

  const loadUsers = async () => {
    try { const { data } = await api.get("/users"); setUsers(data || []); } catch { /* noop */ }
  };
  const loadSettings = async () => {
    try {
      const { data } = await api.get("/auditoria/settings");
      setRetentionDays(data.retention_days || 30);
    } catch { /* noop */ }
  };
  const load = async () => {
    setLoading(true);
    try {
      const params = { limit, skip };
      if (filters.q) params.q = filters.q;
      if (filters.user_id) params.user_id = filters.user_id;
      if (filters.resource) params.resource = filters.resource;
      if (filters.action) params.action = filters.action;
      const { data } = await api.get("/auditoria", { params });
      setItems(data.items || []);
      setTotal(data.total || 0);
    } finally { setLoading(false); }
  };
  useEffect(() => { loadUsers(); loadSettings(); }, []);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [skip, filters]);

  const applyFilter = (patch) => { setSkip(0); setFilters((f) => ({ ...f, ...patch })); };
  const resources = useMemo(() => {
    const s = new Set(items.map((i) => i.resource).filter(Boolean));
    return Array.from(s).sort();
  }, [items]);

  const doRevert = async () => {
    if (!confirmRevert) return;
    try {
      const { data } = await api.post(`/auditoria/${confirmRevert.id}/revertir`);
      toast.success(data.detail || "Revertido");
      setConfirmRevert(null); load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al revertir");
    }
  };

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      const { data } = await api.put("/auditoria/settings", { retention_days: Number(retentionDays) });
      toast.success(`Retención: ${data.retention_days} días`);
      setSettingsOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al guardar");
    } finally { setSavingSettings(false); }
  };

  return (
    <>
      <Topbar
        title="Auditoría de Movimientos"
        subtitle={`${total} evento(s) registrados · retención ${retentionDays} días · auto-purge activo`}
        right={
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={load} className="rounded-sm h-9" data-testid="audit-refresh">
              <ArrowsClockwise size={14} className="mr-1" /> Actualizar
            </Button>
            <Button
              onClick={() => setSettingsOpen(true)}
              className="rounded-sm bg-neutral-800 hover:bg-neutral-900 h-9"
              data-testid="audit-settings-btn"
            >
              <ClockCounterClockwise size={14} className="mr-1" /> Retención
            </Button>
          </div>
        }
      />
      <div className="p-4 space-y-4">
        {/* Filtros */}
        <div className="bg-white border border-[#E5E5E5] rounded-sm p-3 flex flex-wrap gap-3 items-center" data-testid="audit-filters">
          <div className="relative flex-1 min-w-[200px]">
            <MagnifyingGlass size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-neutral-400" />
            <Input
              placeholder="Buscar usuario, ruta o módulo…"
              value={filters.q}
              onChange={(e) => applyFilter({ q: e.target.value })}
              className="rounded-sm h-9 pl-8 text-[13px]"
              data-testid="audit-search"
            />
          </div>
          <Select value={filters.user_id || "__all__"} onValueChange={(v) => applyFilter({ user_id: v === "__all__" ? "" : v })}>
            <SelectTrigger className="rounded-sm h-9 w-48" data-testid="audit-user"><SelectValue placeholder="Todos los usuarios" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">Todos los usuarios</SelectItem>
              {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.username}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.action || "__all__"} onValueChange={(v) => applyFilter({ action: v === "__all__" ? "" : v })}>
            <SelectTrigger className="rounded-sm h-9 w-36" data-testid="audit-action"><SelectValue placeholder="Todas las acciones" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">Todas las acciones</SelectItem>
              <SelectItem value="create">Altas</SelectItem>
              <SelectItem value="update">Modificaciones</SelectItem>
              <SelectItem value="delete">Bajas</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filters.resource || "__all__"} onValueChange={(v) => applyFilter({ resource: v === "__all__" ? "" : v })}>
            <SelectTrigger className="rounded-sm h-9 w-44" data-testid="audit-resource"><SelectValue placeholder="Todos los módulos" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">Todos los módulos</SelectItem>
              {Object.entries(RESOURCE_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
              {resources.filter((r) => !RESOURCE_LABEL[r]).map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {/* Tabla */}
        <div className="border border-[#E5E5E5] bg-white rounded-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead className="bg-neutral-50 text-[11px] uppercase font-mono text-[#666]">
                <tr>
                  <th className="px-4 py-2 text-left w-40">Fecha</th>
                  <th className="px-4 py-2 text-left w-32">Usuario</th>
                  <th className="px-4 py-2 text-left w-24">Acción</th>
                  <th className="px-4 py-2 text-left w-36">Módulo</th>
                  <th className="px-4 py-2 text-left">Detalle del movimiento</th>
                  <th className="px-4 py-2 text-right w-40">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={6} className="text-center py-6 text-[#999]">Cargando…</td></tr>
                )}
                {!loading && items.length === 0 && (
                  <tr><td colSpan={6} className="text-center py-10 text-[#999]">Sin registros para los filtros aplicados.</td></tr>
                )}
                {items.map((it) => {
                  const meta = ACTION_META[it.action] || { label: it.action, color: "bg-neutral-100 text-neutral-800", icon: Warning };
                  const Icon = meta.icon;
                  const summary = summaryFor(it);
                  return (
                    <tr key={it.id} className="border-t border-neutral-100 hover:bg-neutral-50 group" data-testid={`audit-row-${it.id}`}>
                      <td className="px-4 py-2 font-mono text-[12px] text-[#666] align-top">{fmtDate(it.timestamp)}</td>
                      <td className="px-4 py-2 align-top">
                        <div className="flex items-center gap-1.5">
                          <User size={14} weight="duotone" className="text-[#6B1F24]" />
                          <span className="font-medium">{it.username}</span>
                        </div>
                      </td>
                      <td className="px-4 py-2 align-top">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-[11px] font-mono uppercase ${meta.color}`}>
                          <Icon size={11} weight="bold" /> {meta.label}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-[#666] align-top">{RESOURCE_LABEL[it.resource] || it.resource || "-"}</td>
                      <td className="px-4 py-2 align-top">
                        {/* Descripción humana del evento */}
                        <div className="text-[13px] font-medium text-neutral-800" data-testid={`audit-desc-${it.id}`}>
                          {summary.description}
                        </div>
                        {/* Chips de contexto con etiquetas legibles */}
                        {summary.context.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-1">
                            {summary.context.map((c) => (
                              <span key={c.key} className="text-[10px] font-mono bg-neutral-100 text-neutral-700 px-1.5 py-0.5 rounded-sm">
                                <span className="text-neutral-500">{fieldLabel(c.key)}:</span> {fmtByKey(c.key, c.value, summary.snap)}
                              </span>
                            ))}
                          </div>
                        )}
                        {/* Cambios (para update) — con labels y formato por tipo */}
                        {it.action === "update" && summary.changes.length > 0 && (
                          <div className="mt-1 space-y-0.5">
                            {summary.changes.map((c) => (
                              <div key={c.key} className="text-[11px]">
                                <span className="text-neutral-500">{fieldLabel(c.key)}:</span>{" "}
                                <span className="text-rose-700 font-mono">{fmtByKey(c.key, c.before, summary.snap)}</span>
                                <span className="mx-1 text-neutral-400">→</span>
                                <span className="text-emerald-700 font-mono">{fmtByKey(c.key, c.after, summary.snap)}</span>
                              </div>
                            ))}
                          </div>
                        )}
                        {it.reverted && (
                          <div className="text-[10px] font-mono text-emerald-700 mt-0.5">
                            Revertido {it.reverted_by_username ? `por ${it.reverted_by_username}` : ""} · {fmtDate(it.reverted_at)}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-2 text-right align-top">
                        <div className="flex justify-end gap-1 opacity-70 group-hover:opacity-100">
                          <button
                            onClick={() => setDetail(it)}
                            className="p-1.5 hover:bg-neutral-200 rounded-sm text-[#666] hover:text-[#1A1A1A]"
                            data-testid={`audit-view-${it.id}`}
                            title="Ver detalle"
                          >
                            <Eye size={14} />
                          </button>
                          <button
                            onClick={() => setConfirmRevert(it)}
                            disabled={!it.reversible || it.reverted}
                            className="p-1.5 hover:bg-amber-50 rounded-sm text-[#666] hover:text-amber-700 disabled:opacity-30 disabled:cursor-not-allowed"
                            title={
                              it.reverted ? "Ya revertido" :
                              !it.reversible ? "No reversible (entidad de negocio)" :
                              "Revertir"
                            }
                            data-testid={`audit-revert-${it.id}`}
                          >
                            <ArrowCounterClockwise size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Paginación */}
        {total > limit && (
          <div className="flex items-center justify-between text-[12px] text-[#666]">
            <span>Mostrando {skip + 1}–{Math.min(skip + limit, total)} de {total}</span>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" className="rounded-sm" disabled={skip === 0} onClick={() => setSkip(Math.max(0, skip - limit))}>Anterior</Button>
              <Button variant="outline" size="sm" className="rounded-sm" disabled={skip + limit >= total} onClick={() => setSkip(skip + limit)}>Siguiente</Button>
            </div>
          </div>
        )}
      </div>

      {/* Detalle */}
      <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <ShieldCheck size={18} weight="duotone" className="text-[#6B1F24]" />
              Detalle del evento
            </DialogTitle>
            <DialogDescription>
              {detail && (
                <span className="font-mono text-[11px]">
                  {fmtDate(detail.timestamp)} · {detail.username} · {detail.method} {detail.path}
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          {detail && (
            <div className="space-y-3 text-[12px]">
              {/* Resumen destacado del elemento afectado */}
              <div className="border rounded-sm bg-[#F5F1E9]/40 p-3">
                <div className="text-[11px] uppercase font-mono text-[#6B1F24] tracking-widest">
                  {RESOURCE_LABEL[detail.resource] || detail.resource}
                </div>
                <div className="text-[15px] font-heading font-semibold mt-0.5">
                  {summaryFor(detail).description}
                </div>
                {summaryFor(detail).label && (
                  <div className="text-[11px] text-neutral-600 mt-0.5 font-mono">
                    Registro: {summaryFor(detail).label}
                  </div>
                )}
                {summaryFor(detail).context.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {summaryFor(detail).context.map((c) => (
                      <span key={c.key} className="text-[10px] font-mono bg-white border border-neutral-200 text-neutral-700 px-1.5 py-0.5 rounded-sm">
                        <span className="text-neutral-500">{fieldLabel(c.key)}:</span> {fmtByKey(c.key, c.value, summaryFor(detail).snap)}
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div><b>Entidad ID:</b> <span className="font-mono text-[11px]">{detail.entity_id || "-"}</span></div>
                <div><b>Acción:</b> {ACTION_META[detail.action]?.label || detail.action}</div>
                <div><b>Reversible:</b> {detail.reversible ? "Sí" : "No"}</div>
                <div><b>Ruta HTTP:</b> <span className="font-mono text-[10px]">{detail.method} {detail.path}</span></div>
                {detail.reverted && <div className="col-span-2 text-emerald-700"><b>Revertido</b> por {detail.reverted_by_username} el {fmtDate(detail.reverted_at)}</div>}
              </div>
              {detail.action === "update" && detail.before && detail.after && (
                <div>
                  <div className="text-[11px] uppercase font-mono text-neutral-500 mb-1">Cambios ({diffKeys(detail.before, detail.after).length})</div>
                  <div className="border rounded-sm divide-y">
                    {diffKeys(detail.before, detail.after).length === 0 && (
                      <div className="p-2 text-[11px] font-mono text-neutral-400">Sin cambios detectables (payload sin diffs).</div>
                    )}
                    {diffKeys(detail.before, detail.after).map((d) => (
                      <div key={d.key} className="p-2 grid grid-cols-[150px_1fr_1fr] gap-2 text-[11px]">
                        <div className="text-neutral-700 font-medium">{fieldLabel(d.key)}</div>
                        <div className="text-rose-700 font-mono truncate" title={JSON.stringify(d.before)}>{fmtByKey(d.key, d.before, detail.after)}</div>
                        <div className="text-emerald-700 font-mono truncate" title={JSON.stringify(d.after)}>{fmtByKey(d.key, d.after, detail.after)}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {detail.payload && (
                <details>
                  <summary className="text-[11px] uppercase font-mono text-neutral-500 cursor-pointer">Payload enviado</summary>
                  <pre className="mt-1 bg-neutral-50 border rounded-sm p-2 text-[11px] font-mono max-h-56 overflow-auto">
{JSON.stringify(detail.payload, null, 2)}
                  </pre>
                </details>
              )}
              {detail.before && (
                <details>
                  <summary className="text-[11px] uppercase font-mono text-neutral-500 cursor-pointer">Snapshot previo</summary>
                  <pre className="mt-1 bg-neutral-50 border rounded-sm p-2 text-[11px] font-mono max-h-56 overflow-auto">
{JSON.stringify(detail.before, null, 2)}
                  </pre>
                </details>
              )}
              {detail.after && (
                <details>
                  <summary className="text-[11px] uppercase font-mono text-neutral-500 cursor-pointer">Snapshot posterior</summary>
                  <pre className="mt-1 bg-neutral-50 border rounded-sm p-2 text-[11px] font-mono max-h-56 overflow-auto">
{JSON.stringify(detail.after, null, 2)}
                  </pre>
                </details>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDetail(null)}>Cerrar</Button>
            {detail?.reversible && !detail?.reverted && (
              <Button onClick={() => { setConfirmRevert(detail); setDetail(null); }} className="bg-amber-600 hover:bg-amber-700">
                <ArrowCounterClockwise size={14} className="mr-1" /> Revertir
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirmar revert */}
      <AlertDialog open={!!confirmRevert} onOpenChange={(v) => !v && setConfirmRevert(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Revertir movimiento?</AlertDialogTitle>
            <AlertDialogDescription>
              {confirmRevert?.action === "create" && "Se eliminará el registro creado."}
              {confirmRevert?.action === "update" && "Se restaurarán los valores previos del registro."}
              {confirmRevert?.action === "delete" && "Se recreará el registro con el snapshot previo."}
              {confirmRevert && (
                <div className="mt-3 border rounded-sm p-2 bg-neutral-50 text-neutral-800">
                  <div className="text-[11px] font-mono uppercase text-neutral-500">
                    {RESOURCE_LABEL[confirmRevert.resource] || confirmRevert.resource}
                  </div>
                  <div className="font-medium text-[13px] mt-0.5">
                    {summaryFor(confirmRevert).description}
                  </div>
                  <div className="text-[10px] font-mono text-neutral-500 mt-1">
                    {confirmRevert.username} · {fmtDate(confirmRevert.timestamp)}
                  </div>
                </div>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={doRevert} className="bg-amber-600 hover:bg-amber-700" data-testid="confirm-revert">
              Revertir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Settings: retención */}
      <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">Retención de auditoría</DialogTitle>
            <DialogDescription>
              Los eventos más antiguos que esta cantidad de días se borran automáticamente.
            </DialogDescription>
          </DialogHeader>
          <div className="py-3 space-y-2">
            <Label className="text-[12px]">Días</Label>
            <NumberInput
              min={1}
              max={3650}
              value={retentionDays}
              onChange={(e) => setRetentionDays(e.target.value)}
              className="rounded-sm font-mono"
              data-testid="audit-retention-input"
            />
            <div className="text-[10px] font-mono text-[#999]">
              El TTL de MongoDB purga registros según su timestamp. El cambio se aplica de inmediato.
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSettingsOpen(false)}>Cancelar</Button>
            <Button onClick={saveSettings} disabled={savingSettings} className="bg-[#6B1F24] hover:bg-[#571519]" data-testid="audit-retention-save">
              {savingSettings ? "Guardando…" : "Guardar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
